import { useLocation, useNavigate } from 'react-router';

import { Brand } from '@/components/layout';
import { Alert, Button, Field, Input, PasswordInput } from '@/components/ui';
import { useLogin } from '@/features/auth/api/auth.api';
import { useForm } from '@/hooks/use-form';
import { isApiError } from '@/lib/api-error';
import { loginSchema } from '@/lib/validation';

interface LoginValues extends Record<string, unknown> {
  identifier: string;
  password: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();

  /* Set by RequireAuth so sign-in returns the user where they were headed. */
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const form = useForm<LoginValues>({
    initialValues: { identifier: '', password: '' },
    schema: loginSchema,
    onSubmit: (values) => {
      login.mutate(values, {
        onSuccess: ({ user }) => {
          void navigate(user.mustChangePassword ? '/change-password' : from, { replace: true });
        },
      });
    },
  });

  const errorMessage = isApiError(login.error)
    ? login.error.message
    : login.error
      ? 'Something went wrong. Please try again.'
      : null;

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <Brand size="lg" asLink={false} className="mb-4" />
          <h1 className="text-lg font-bold tracking-tight text-content">Sign in</h1>
          <p className="mt-1 text-sm text-content-muted">
            Use the credentials issued by your administrator.
          </p>
        </div>

        <form
          onSubmit={form.handleSubmit}
          /* The browser's own validation bubbles are unstyleable and fire
             before ours, so they are switched off. */
          noValidate
          className="space-y-3 rounded-md border border-border bg-surface p-5"
        >
          {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

          <Field label="Username or email" error={form.errors.identifier} required>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                data-field="identifier"
                aria-describedby={describedBy}
                invalid={invalid}
                value={form.values.identifier}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                iconLeft="user"
                onChange={(event) => {
                  form.setField('identifier', event.target.value);
                }}
                onBlur={() => {
                  form.touch('identifier');
                }}
              />
            )}
          </Field>

          <Field label="Password" error={form.errors.password} required>
            {({ id, describedBy, invalid }) => (
              <PasswordInput
                id={id}
                data-field="password"
                aria-describedby={describedBy}
                invalid={invalid}
                value={form.values.password}
                autoComplete="current-password"
                onChange={(event) => {
                  form.setField('password', event.target.value);
                }}
                onBlur={() => {
                  form.touch('password');
                }}
              />
            )}
          </Field>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={login.isPending}
            className="mt-1"
          >
            Sign in
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-content-subtle">
          WinDocs · Document-centric CRM
        </p>
      </div>
    </div>
  );
}
